import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Tool } from '@/types/tool';
import { setupLocationSelection } from './mapUtils';
import MapLoadingState from './MapLoadingState';
import MapControls from './MapControls';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { initializeMap } from './MapConfiguration';
import { setupMarkers, addSelectedLocationMarker } from './MarkerManager';

interface MapInitializerProps {
  tools: Tool[];
  onLocationSelect?: (location: { lat: number; lng: number; formattedAddress: string }) => void;
  selectedLocation?: { lat: number; lng: number };
  isSelectingLocation?: boolean;
  defaultCenter?: { lat: number; lng: number };
  defaultZoom?: number;
  mapRef?: React.MutableRefObject<mapboxgl.Map | null>;
  isMobile?: boolean;
}

const MapInitializer = ({ 
  tools, 
  onLocationSelect, 
  selectedLocation, 
  isSelectingLocation,
  defaultCenter,
  defaultZoom,
  mapRef: externalMapRef
}: MapInitializerProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const internalMapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const selectedMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const [isMapReady, setIsMapReady] = useState(false);
  const isInitializedRef = useRef(false);

  const map = externalMapRef || internalMapRef;

  // Cleanup function
  const cleanup = () => {
    if (markersRef.current) {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    }
    
    if (selectedMarkerRef.current) {
      selectedMarkerRef.current.remove();
      selectedMarkerRef.current = null;
    }
    
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    isInitializedRef.current = false;
  };

  useEffect(() => {
    const initializeMapInstance = async () => {
      if (!mapContainer.current || isInitializedRef.current) return;

      try {
        cleanup();

        const { data: { MAPBOX_TOKEN }, error: tokenError } = await supabase
          .functions.invoke('get-mapbox-token');

        if (tokenError || !MAPBOX_TOKEN) {
          console.error('Error fetching Mapbox token:', tokenError);
          setError('Failed to load map configuration');
          setIsLoading(false);
          toast({
            title: "Error",
            description: "Failed to load map. Please try again later.",
            variant: "destructive",
          });
          return;
        }

        mapboxgl.accessToken = MAPBOX_TOKEN;

        map.current = await initializeMap({ 
          container: mapContainer.current,
          selectedLocation,
          defaultCenter,
          defaultZoom
        });

        isInitializedRef.current = true;

        map.current.on('load', () => {
          setIsMapReady(true);
          setIsLoading(false);
          setError(null);
        });

        map.current.on('error', (e) => {
          console.error('Map error:', e);
          setError('An error occurred with the map');
          toast({
            title: "Map Error",
            description: "There was an issue loading the map. Please try refreshing the page.",
            variant: "destructive",
          });
        });

      } catch (error) {
        console.error('Error initializing map:', error);
        setError('Failed to initialize map');
        setIsLoading(false);
        toast({
          title: "Error",
          description: "Failed to load map. Please try again later.",
          variant: "destructive",
        });
      }
    };

    initializeMapInstance();

    return cleanup;
  }, []);

  useEffect(() => {
    if (!isMapReady || !map.current) return;

    const setupMapFeatures = async () => {
      try {
        const validTools = tools.filter(tool => 
          tool.latitude && tool.longitude && 
          !isNaN(tool.latitude) && !isNaN(tool.longitude)
        );

        if (validTools.length > 0) {
          markersRef.current = await setupMarkers(validTools, map.current!);
          
          const bounds = new mapboxgl.LngLatBounds();
          validTools.forEach(tool => {
            if (tool.latitude && tool.longitude) {
              bounds.extend([tool.longitude, tool.latitude]);
            }
          });
          
          map.current.fitBounds(bounds, {
            padding: 50,
            maxZoom: 15
          });
        }

        if (selectedLocation) {
          selectedMarkerRef.current = addSelectedLocationMarker(
            map.current!,
            selectedLocation.lat,
            selectedLocation.lng
          );
        }

        if (isSelectingLocation && onLocationSelect) {
          const cleanup = setupLocationSelection(map.current!, async (lat, lng) => {
            try {
              const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`
              );
              
              if (!response.ok) throw new Error('Failed to fetch address');
              
              const data = await response.json();
              const address = data.features?.[0]?.place_name || '';

              if (selectedMarkerRef.current) {
                selectedMarkerRef.current.remove();
              }
              
              selectedMarkerRef.current = addSelectedLocationMarker(map.current!, lat, lng);
              
              onLocationSelect({
                lat,
                lng,
                formattedAddress: address
              });
            } catch (error) {
              console.error('Error getting address:', error);
              toast({
                title: "Error",
                description: "Failed to get address. Please try again.",
                variant: "destructive",
              });
            }
          });
          
          return cleanup;
        }
      } catch (error) {
        console.error('Error setting up map features:', error);
        toast({
          title: "Warning",
          description: "Some map features failed to load. You may need to refresh the page.",
          variant: "destructive",
        });
      }
    };

    setupMapFeatures();
  }, [isMapReady, tools, selectedLocation, isSelectingLocation, onLocationSelect]);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div ref={mapContainer} className="w-full h-full rounded-lg" />
      {map.current && <MapControls map={map.current} />}
      <MapLoadingState isLoading={isLoading} />
    </>
  );
};

export default MapInitializer;